/**
 * Search improvements (Fratticioli):
 *
 * 1. Stricter product matching. Shopify's default search returns every product matching ANY of the words (searching
 *    "nikon z6" returns 150+ products). For queries of 2 words or more, we require EVERY word to be found in the title,
 *    product type, vendor, tags or SKU. Single words are left to the standard search, which also matches partial words
 *    ("fuji" finds "Fujifilm") while the strict syntax only matches whole words. When the strict query finds nothing,
 *    the theme falls back to the standard search (see "main-search" section and the predictive search in theme.js).
 *
 * 2. On touch devices, Prestige does not focus the search input when the header search opens, so the keyboard does not
 *    show up. We focus it synchronously while the tap is being handled, which is required by iOS to open the keyboard.
 */
(() => {
  const STRICT_FIELDS = ['title', 'product_type', 'vendor', 'tag', 'variants.sku'];
  const STOP_WORDS = new Set(['di', 'da', 'in', 'con', 'su', 'per', 'tra', 'fra', 'il', 'lo', 'la', 'gli', 'le', 'un', 'uno', 'una', 'ed', 'del', 'dello', 'della', 'dei', 'degli', 'delle', 'dell', 'al', 'allo', 'alla', 'ai', 'agli', 'alle', 'all', 'nel', 'nella', 'and', 'the', 'for', 'with']);

  /**
   * Return the strict version of a query, or null if the query should be sent as is
   */
  function toStrictQuery(query) {
    // The customer (or the theme) already uses the search syntax: we do not touch it
    if (!query || /[:()"]/.test(query)) {
      return null;
    }

    const words = query
      .split(/[^\p{L}\p{N}.\-]+/u)
      .map((word) => word.replace(/^[.\-]+|[.\-]+$/g, ''))
      .filter((word) => /^\d+$/.test(word) || (word.length > 1 && !STOP_WORDS.has(word.toLowerCase())));

    if (words.length < 2) {
      return null;
    }

    return words.map((word) => {
      // Titles are written both "50mm" and "50 mm", "f1.8" and "f/1.8": we also search the number alone. The word as
      // typed must stay first, as it is the one displayed back on the search page (see "search-display-terms" snippet)
      const forms = [...new Set([word, word.replace(/^(\d[\d.\-]*)mm$/i, '$1').replace(/^f(\d[\d.]*)$/i, '$1')])];
      return `(${forms.flatMap((form) => STRICT_FIELDS.map((field) => `${field}:${form}`)).join(' OR ')})`;
    }).join(' AND ');
  }

  window.themeSearch = { toStrictQuery };

  // Send the strict query when a search form is submitted (header search, "View all results", search page)
  document.addEventListener('submit', (event) => {
    const form = event.target;

    if (event.defaultPrevented || form.method !== 'get' || new URL(form.action).pathname !== `${window.Shopify.routes.root}search`) {
      return;
    }

    const strictQuery = toStrictQuery(form.elements['q']?.value.trim());

    if (!strictQuery) {
      return;
    }

    event.preventDefault();

    const params = new URLSearchParams(new FormData(form, event.submitter));
    params.set('q', strictQuery);
    window.location.assign(`${form.action}?${params}`);
  });

  // Open the keyboard on touch devices when the header search is opened
  document.addEventListener('dialog:before-show', (event) => {
    const supportsHover = window.themeVariables?.mediaQueries?.['supports-hover'] || '(hover: hover)';

    if (event.target.matches('header-search') && !window.matchMedia(supportsHover).matches) {
      event.target.querySelector('input[type="search"]')?.focus({ preventScroll: true });
    }
  }, { capture: true });
})();
